import { useState } from "react"
import { Formik, Form } from "formik"
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import * as Yup from "yup"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import TableVisualization from "~/ui-rtk/components/ui/charts/TableVisualization"
import {
  type TableVisualizationColumn,
  type TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import {
  useKeyEventControllerCreateKeyEventMutation,
  useKeyEventControllerGetAllKeyEventsQuery,
  useKeyEventControllerUpdateKeyEventMutation,
  useKeyEventControllerDeleteKeyEventMutation,
  type KeyEventDto,
} from "~/ui-rtk/api/keyEventsApi"
import { Button } from "~/ui-rtk/components/ui/controls"
import KeyEventFormField from "~/ui-rtk/components/features/settings/KeyEventFormField"
import { toCurrency } from "~/ui-rtk/utils/number-utils"
import useDate from "~/ui-rtk/hooks/date"

const dayjs = useDate()

interface KeyEvent {
  id: string
  name: string
  type: string
  spend?: number
  start: Date
  end?: Date
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  type: Yup.string().required("Type is required"),
  spend: Yup.string()
    .nullable()
    .test("is-valid-currency", "Please enter a valid amount", value => {
      if (!value) return true
      const numericValue = parseFloat(value.toString().replace(/[^0-9.]/g, ""))
      return !isNaN(numericValue)
    }),
  start: Yup.date()
    .required("Start date is required")
    .test(
      "start-before-end",
      "Start date must be before end date",
      function (value) {
        const end = this.parent.end
        if (!end) return true
        return value <= end
      },
    ),
  end: Yup.date()
    .nullable()
    .test(
      "end-after-start",
      "End date must be after start date",
      function (value) {
        if (!value) return true
        return value >= this.parent.start
      },
    ),
})

const KeyEventsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<KeyEvent | null>(null)
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const { data: eventsData = [], isLoading } =
    useKeyEventControllerGetAllKeyEventsQuery()
  const [createKeyEvent] = useKeyEventControllerCreateKeyEventMutation()
  const [updateKeyEvent] = useKeyEventControllerUpdateKeyEventMutation()
  const [deleteKeyEvent] = useKeyEventControllerDeleteKeyEventMutation()

  const handleEdit = (event: KeyEvent) => {
    setSelectedEvent({
      ...event,
      start: new Date(event.start),
      end: event.end ? new Date(event.end) : undefined,
    })
    setIsModalOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this key event? This action cannot be undone.",
      )
    ) {
      try {
        await deleteKeyEvent({ id }).unwrap()
      } catch (error) {
        console.error("Error deleting key event:", error)
      }
    }
  }

  const columnConfig: TableVisualizationColumn[] = [
    {
      key: "name",
      header: "Name",
      type: "title",
    },
    {
      key: "type",
      header: "Type",
      type: "auto",
    },
    {
      key: "spend",
      header: "Spend",
      type: "auto",
      render: row =>
        row.spend ? toCurrency(row.spend as string | number) : "",
    },
    {
      key: "start",
      header: "Start Date",
      type: "date",
    },
    {
      key: "end",
      header: "End Date",
      type: undefined,
      render: row =>
        row.end &&
        dayjs(new Date(row.end as string | number | Date)).isValid() ? (
          <p>
            {dayjs(new Date(row.end as string | number | Date)).format(
              "YYYY-MM-DD",
            )}
          </p>
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      header: "Actions",
      type: undefined,
      render: row => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleEdit(row as unknown as KeyEvent)}
            className="text-white hover:text-basic-blue transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(row.id as string)}
            className="text-white hover:text-red-500 transition-colors p-1 rounded-md"
          >
            Archive
          </button>
        </div>
      ),
    },
  ]

  const initialValues: Omit<KeyEvent, "id"> = {
    name: "",
    type: "",
    spend: undefined,
    start: new Date(),
    end: undefined,
  }

  const handleSubmit = async (
    values: Omit<
      KeyEventDto,
      "id" | "company_id" | "created_at" | "deleted_at"
    >,
  ) => {
    try {
      if (selectedEvent) {
        await updateKeyEvent({
          id: selectedEvent.id,
          updateKeyEventDto: values,
        }).unwrap()
      } else {
        await createKeyEvent({
          createKeyEventDto: values,
        }).unwrap()
      }
      setIsModalOpen(false)
      setSelectedEvent(null)
    } catch (error) {
      console.error("Error saving key event:", error)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedEvent(null)
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Key Events</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Key Event
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <TableVisualization
          metrics={eventsData as unknown as TMetric[]}
          columnConfig={columnConfig}
          paginationState={paginationState}
          setPaginationState={setPaginationState}
          enablePagination
          chartId="key-events-table"
        />
      )}

      <Dialog
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        width={425}
        closeOnClickOutside={true}
        classes={{
          container:
            "bg-basic-dark-blue border border-basic-blue rounded-lg p-6 shadow-deep",
        }}
      >
        <div className="w-full">
          <div className="flex flex-col gap-2 mb-6 text-start">
            <div className="flex justify-between items-center">
              <h2 className="text-md font-bold leading-none text-white">
                {selectedEvent ? "Edit Key Event" : "Add Key Event"}
              </h2>
              <XMarkIcon
                className="w-5 h-5 text-white cursor-pointer"
                onClick={handleCloseModal}
              />
            </div>
            <p className="text-sm text-gray-400">
              {selectedEvent
                ? "Update your milestone event details here. Key events help track important business milestones and their associated metrics."
                : "Create a new milestone event here. Key events help track important business milestones and their associated metrics."}
            </p>
          </div>

          <Formik
            initialValues={selectedEvent || initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              // Format spend value before submission
              const formattedValues = {
                ...values,
                spend: values.spend
                  ? parseFloat(values.spend.toString().replace(/[^0-9.]/g, ""))
                  : undefined,
              }
              await handleSubmit(formattedValues)
              setSubmitting(false)
              handleCloseModal()
            }}
            enableReinitialize
          >
            {({ isSubmitting, errors, touched }) => (
              <Form className="grid gap-4">
                <div className="grid gap-3">
                  <KeyEventFormField
                    id="name"
                    type="text"
                    label="Name"
                    placeholder="Enter event name"
                    errors={errors as Record<string, string>}
                    touched={touched as Record<string, boolean>}
                  />

                  <KeyEventFormField
                    id="type"
                    type="text"
                    label="Type"
                    placeholder="Enter event type"
                    errors={errors as Record<string, string>}
                    touched={touched as Record<string, boolean>}
                  />

                  <KeyEventFormField
                    id="spend"
                    type="text"
                    label="Spend"
                    placeholder="Enter spend amount"
                    errors={errors as Record<string, string>}
                    touched={touched as Record<string, boolean>}
                  />

                  <div className="flex gap-2">
                    <KeyEventFormField
                      id="start"
                      type="date"
                      label="Start Date"
                      errors={errors as Record<string, string>}
                      touched={touched as Record<string, boolean>}
                    />

                    <KeyEventFormField
                      id="end"
                      type="date"
                      label="End Date"
                      errors={errors as Record<string, string>}
                      touched={touched as Record<string, boolean>}
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end mt-3">
                  <Button
                    type="button"
                    variant={{
                      variant: "outlined",
                    }}
                    onClick={handleCloseModal}
                    className="p-0 h-auto"
                  >
                    <p className="text-white px-6 py-2 text-sm">Cancel</p>
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant={{
                      variant: "solid",
                      color: "pink",
                    }}
                    className="p-0 h-auto"
                  >
                    <p className="text-white px-6 py-2 text-sm">
                      {selectedEvent ? "Update" : "Create"}
                    </p>
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Dialog>
    </div>
  )
}

export default KeyEventsPage
